import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './App.css';
import AddressQueue from './components/AddressQueue';
import AwaitingPanel, { documentKey } from './components/AwaitingPanel';
import DataHealth from './components/DataHealth';
import Analytics from './components/Analytics';
import DeliveryPanel from './components/DeliveryPanel';
import DestinationFixer from './components/DestinationFixer';
import Obstacles from './components/Obstacles';
import CorridorQueue from './components/CorridorQueue';
import FieldMonitor from './components/FieldMonitor';
import ProposalPanel from './components/ProposalPanel';
import TemplatePicker from './components/TemplatePicker';
import TourBuilder from './components/TourBuilder';
import { api } from './services/api';
import { useSignedIn } from './session';
import {
  AwaitingItem,
  Delivery,
  DestinationGroup,
  Driver,
  NetworkLocation,
  Reconciliation,
  RouteTemplate,
  TourProposal,
  TourStopSelection,
  Vehicle
} from './types';

const DEFAULT_MAX_WAIT_DAYS = 1;
const DEFAULT_FILL_THRESHOLD = 60;

type View = 'planiranje' | 'adrese' | 'teren' | 'pravci' | 'analitika' | 'prepreke';

/**
 * Izbor se pamti kao lista ključeva sa prefiksom tipa. Tura je mešovita, pa MPO
 * i kupac moraju stajati u istoj listi da bi redosled obilaska bio jedan — a
 * prefiks postoji da se šifra objekta i ključ otpremnice ne mogu pomešati.
 */
const MPO = 'mpo:';
const KUPAC = 'kupac:';

/**
 * Preuzimanje ima svoj prefiks iako je i ono lokacija iz mreže. Bez toga bi
 * „podigni aparat u Apatinu" i „ostavi prenos za Apatin" delili isti ključ, pa
 * bi jedno gasilo drugo — a to su dve različite stanice na istoj turi.
 */
const PREUZIMANJE = 'preuzimanje:';

interface Props {
  onSignOut: () => void;
}

export default function App({ onSignOut }: Props) {
  const signedIn = useSignedIn();
  const [view, setView] = useState<View>('planiranje');

  /**
   * Nalozi koje je operater skinuo sa ture jer tovar ne staje.
   *
   * Drži se ovde, a ne u izboru stanica, jer stanica ostaje ista — menja se
   * samo šta se na nju tovari. Ključ je „odredište|nalog".
   */
  const [excludedDocuments, setExcludedDocuments] = useState<string[]>([]);
  const [items, setItems] = useState<AwaitingItem[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [report, setReport] = useState<Reconciliation | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [locations, setLocations] = useState<NetworkLocation[]>([]);
  const [templates, setTemplates] = useState<RouteTemplate[]>([]);
  const [maxWaitDays, setMaxWaitDays] = useState(DEFAULT_MAX_WAIT_DAYS);
  const [fillThreshold, setFillThreshold] = useState(DEFAULT_FILL_THRESHOLD);
  const [selected, setSelected] = useState<string[]>([]);
  const [proposal, setProposal] = useState<TourProposal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const settings = await api.settings().catch(() => null);

      const waitDays = Number(settings?.['max_wait_days_interni']?.value);
      const effectiveWaitDays = Number.isFinite(waitDays) && waitDays > 0 ? waitDays : DEFAULT_MAX_WAIT_DAYS;
      setMaxWaitDays(effectiveWaitDays);

      const threshold = Number(settings?.['popunjenost_threshold']?.value);
      setFillThreshold(Number.isFinite(threshold) && threshold > 0 ? threshold : DEFAULT_FILL_THRESHOLD);

      // B2C ima svoj prag čekanja — kupac koji je platio čeka drugačije nego
      // maloprodajni objekat koji se dopunjava.
      const b2cWaitDays = Number(settings?.['max_wait_days_b2c']?.value);
      const effectiveB2cWaitDays = Number.isFinite(b2cWaitDays) && b2cWaitDays > 0 ? b2cWaitDays : 2;

      const [awaiting, pending, reconciliation, fleet, staff, network, routes] = await Promise.all([
        api.awaiting(effectiveWaitDays),
        api.deliveries(effectiveB2cWaitDays).catch(() => []),
        api.reconciliation().catch(() => null),
        api.vehicles(),
        api.drivers(),
        api.locations().catch(() => []),
        api.templates().catch(() => [])
      ]);

      setItems(awaiting);
      setDeliveries(pending);
      setReport(reconciliation);
      setVehicles(fleet);
      setDrivers(staff);
      setLocations(network);
      setTemplates(routes);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const groups = useMemo<DestinationGroup[]>(() => {
    const byDestination = new Map<string, AwaitingItem[]>();

    for (const item of items) {
      const key = item.destination ?? 'Bez odredišta — traži potvrdu';
      const bucket = byDestination.get(key);
      if (bucket) bucket.push(item);
      else byDestination.set(key, [item]);
    }

    return Array.from(byDestination.entries())
      .map(([destination, groupItems]) => {
        const overdue = groupItems.filter((i) => i.isOverdue);
        return {
          destination,
          items: groupItems,
          totalQuantity: groupItems.reduce((sum, i) => sum + i.quantity, 0),
          oldestWaitingDays: Math.max(...groupItems.map((i) => i.waitingDays)),
          overdueCount: overdue.length,
          overdueQuantity: overdue.reduce((sum, i) => sum + i.quantity, 0),
          hasOverdue: overdue.length > 0,
          needsReviewCount: groupItems.filter((i) => i.needsReview).length
        };
      })
      // Prednost ima odredište gde najviše robe prelazi prag čekanja, ne ono
      // koje ima jedan zaboravljen komad od pre tri meseca.
      .sort((a, b) => {
        if (a.overdueQuantity !== b.overdueQuantity) return b.overdueQuantity - a.overdueQuantity;
        return b.totalQuantity - a.totalQuantity;
      });
  }, [items]);

  /**
   * Učitava šablon kao polaznu tačku. Stanice bez robe se izostavljaju — nema
   * smisla voziti do mesta gde ništa ne čeka, a operater ih uvek može dodati.
   */
  /**
   * Ključ stanice iz predloga, u istom obliku u kom se pamti izbor. Prazno kad
   * stanica nema po čemu da se prepozna — takva se ne može ni dodati ni izbaciti.
   */
  const keyOfProposedStop = useCallback(
    (stop: {
      kind: string;
      documentKey: string | null;
      locationKind: string | null;
      locationCode: string | null;
      isPickup?: boolean;
    }): string | null => {
      if (stop.kind === 'CustomerAddress') {
        return stop.documentKey ? KUPAC + stop.documentKey : null;
      }

      if (!stop.locationCode) return null;

      // Radna lista označava odredište kao „MPO 003" / „MAG 021".
      const prefix = stop.locationKind === 'Warehouse' ? 'MAG' : 'MPO';
      const destination = `${prefix} ${stop.locationCode}`;

      return stop.isPickup ? PREUZIMANJE + destination : MPO + destination;
    },
    []
  );

  /**
   * Učitava predlog za pravac i njime puni turu.
   *
   * Predlog je polazna tačka, ne naredba — sve se i dalje dodaje, izbacuje i
   * preređuje. Zato dugme kaže „predlog", a ne „primeni".
   */
  const applyTemplate = async (template: RouteTemplate) => {
    setError(null);
    try {
      const built = await api.proposal(template.id);
      setProposal(built);
      setSelected(
        built.stops
          .map((stop) => keyOfProposedStop(stop))
          .filter((key): key is string => key !== null)
      );
    } catch (e) {
      // Bez predloga se pravac i dalje može učitati po starom — stanice sa robom.
      setError(e instanceof Error ? e.message : String(e));
      setProposal(null);
      setSelected(
        template.stops
          .map((s) => s.destination)
          .filter((destination) => groups.some((g) => g.destination === destination))
          .map((destination) => MPO + destination)
      );
    }
  };

  /**
   * Dodaje ili izbacuje stanicu.
   *
   * <paramref name="position"/> je mesto na koje stanica pripada po računici —
   * bez njega je svaka dodata stanica završavala na KRAJU ture. Zbog toga se
   * dešavalo da tura prođe kroz Apatin, ode u Sontu, pa se vrati u Apatin na
   * adrese koje je operater dodao klikom.
   */
  const toggle = (key: string, position?: number) =>
    setSelected((current) => {
      if (current.includes(key)) {
        return current.filter((k) => k !== key);
      }

      if (position === undefined || position < 0 || position > current.length) {
        return [...current, key];
      }

      const next = [...current];
      next.splice(position, 0, key);
      return next;
    });

  const toggleDocument = (destination: string, document: string | null) => {
    const key = documentKey(destination, document);

    const next = excludedDocuments.includes(key)
      ? excludedDocuments.filter((k) => k !== key)
      : [...excludedDocuments, key];

    setExcludedDocuments(next);

    // Odredište kome je skinut i poslednji nalog prestaje da bude stanica.
    // Inače bi na spisku ture stajalo mesto na koje se ništa ne vozi.
    const group = groups.find((g) => g.destination === destination);
    const anythingLeft = group?.items.some(
      (item) => !next.includes(documentKey(destination, item.sourceDocument)));

    if (group && !anythingLeft) {
      setSelected((current) => current.filter((k) => k !== MPO + destination));
    }
  };

  /**
   * Prihvata predloženi redosled stanica.
   *
   * Ključevi koji u predlog nisu ušli — stanica bez šifre, ili nešto što je u
   * međuvremenu otpalo — ostaju na kraju umesto da tiho nestanu iz ture.
   */
  const reorder = (keys: string[]) =>
    setSelected((current) => [...keys, ...current.filter((k) => !keys.includes(k))]);

  const move = (key: string, direction: -1 | 1) =>
    setSelected((current) => {
      const index = current.indexOf(key);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.length) return current;

      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  // Redosled izbora je redosled obilaska, pa se stanice ne sortiraju ponovo.
  // Ključ koji više nema pokriće — roba otišla, isporuka rezervisana — tiho
  // ispada, jer bi turi dodao stanicu bez sadržaja.
  const selectedStops = useMemo<TourStopSelection[]>(
    () =>
      selected
        .map((key): TourStopSelection | null => {
          if (key.startsWith(KUPAC)) {
            const delivery = deliveries.find((d) => KUPAC + d.documentKey === key);
            return delivery ? { kind: 'kupac', key, delivery } : null;
          }

          // Preuzimanje ne postoji ni u jednoj radnoj listi — nastalo je u
          // predlogu, iz odluke odakle roba ide. Zato se traži tamo.
          if (key.startsWith(PREUZIMANJE)) {
            const stop = proposal?.stops.find((s) => keyOfProposedStop(s) === key);
            return stop ? { kind: 'preuzimanje', key, stop } : null;
          }

          const group = groups.find((g) => MPO + g.destination === key);
          if (!group) return null;

          // Izbačeni nalozi otpadaju ovde, na jednom mestu — pa ih ni procena
          // popunjenosti, ni redosled utovara, ni sačuvana tura ne vide. Panel
          // ih i dalje prikazuje precrtane, da operater može da se predomisli.
          const items = group.items.filter(
            (item) => !excludedDocuments.includes(documentKey(group.destination, item.sourceDocument)));

          // Odredište kome je skinuto sve nije stanica bez sadržaja nego stanica
          // koje nema.
          if (items.length === 0) return null;

          return {
            kind: 'mpo',
            key,
            group: {
              ...group,
              items,
              totalQuantity: items.reduce((sum, i) => sum + i.quantity, 0)
            }
          };
        })
        .filter((s): s is TourStopSelection => s !== null),
    [selected, groups, deliveries, proposal, keyOfProposedStop, excludedDocuments]
  );

  return (
    <div className="app">
      <header className="app__header">
        <div>
          <h1>Elbraco — planiranje transporta</h1>
          <p className="muted">
            {view === 'planiranje'
              && 'Interni prenosi i isporuke kupcima idu u istu turu, na istom pravcu.'}
            {view === 'adrese'
              && 'Adrese kupaca koje nisu geokodirane dovoljno sigurno da uđu u rutu.'}
            {view === 'teren'
              && 'Šta se dešava na putu i šta čeka tvoju odluku.'}
            {view === 'pravci'
              && 'Mesta kroz koja pravci prolaze. Dok nisu potvrđena, pojas se ne računa.'}
            {view === 'analitika'
              && 'Koliko tura stvarno traje, mereno po stanici — i koliko to odstupa od procene.'}
            {view === 'prepreke'
              && 'Roba i isporuke koje ne mogu u turu — i šta treba uraditi da mogu.'}
          </p>
        </div>
        <div className="app__who">
          <span className="muted">{signedIn.displayName}</span>
          <button type="button" className="link" onClick={onSignOut}>Odjavi se</button>
        </div>
        {view === 'planiranje' && (
          <button type="button" onClick={() => void load()} disabled={loading}>
            {loading ? 'Učitavam…' : 'Osveži'}
          </button>
        )}
      </header>

      <nav className="tabs">
        <button
          type="button"
          className={view === 'planiranje' ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
          onClick={() => setView('planiranje')}
        >
          Planiranje ture
        </button>
        <button
          type="button"
          className={view === 'adrese' ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
          onClick={() => setView('adrese')}
        >
          Adrese za potvrdu
        </button>
        <button
          type="button"
          className={view === 'teren' ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
          onClick={() => setView('teren')}
        >
          Sa terena
        </button>
        <button
          type="button"
          className={view === 'pravci' ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
          onClick={() => setView('pravci')}
        >
          Pravci
        </button>
        <button
          type="button"
          className={view === 'prepreke' ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
          onClick={() => setView('prepreke')}
        >
          Ne može u turu
        </button>
        <button
          type="button"
          className={view === 'analitika' ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
          onClick={() => setView('analitika')}
        >
          Analitika
        </button>
      </nav>

      {view === 'prepreke' ? (
        <Obstacles />
      ) : view === 'analitika' ? (
        <Analytics />
      ) : view === 'pravci' ? (
        <CorridorQueue />
      ) : view === 'adrese' ? (
        <AddressQueue />
      ) : view === 'teren' ? (
        <FieldMonitor />
      ) : (
        <>
          {error && <div className="health health--bad">{error}</div>}
          <DataHealth report={report} />

          {/* Predlog stoji preko cele širine, iznad radnih lista. U desnoj
              koloni od 420 px mapa nema prostora — čiode se preklapaju i
              oblik ture se ne vidi, a to je jedino zbog čega mapa i postoji. */}
          {proposal && (
            <ProposalPanel
              proposal={proposal}
              selected={selected}
              keyOf={keyOfProposedStop}
              onToggle={toggle}
              onClose={() => setProposal(null)}
            />
          )}

          <div className="app__body">
            {/* Dve radne liste stoje jedna pored druge. Ranije su bile jedna
                ispod druge, pa je spisak internih prenosa gurao isporuke kupcima
                ispod pregiba — a to su dve polovine istog posla. */}
            <div className="worklists">
              <div className="worklists__col">
              <section className="panel">
                <h2>Interni prenosi</h2>
                {loading ? (
                  <p className="muted">Učitavam…</p>
                ) : (
                  <AwaitingPanel
                    groups={groups}
                    selected={selected.filter((k) => k.startsWith(MPO)).map((k) => k.slice(MPO.length))}
                    onToggle={(destination) => toggle(MPO + destination)}
                    maxWaitDays={maxWaitDays}
                    excluded={excludedDocuments}
                    onToggleDocument={toggleDocument}
                  />
                )}
              </section>

              {/* Nalozi bez odredišta stoje uz internu listu, jer su njen deo
                  koji se ne može planirati dok se ne razreši. */}
              <DestinationFixer onRecorded={() => void load()} />
              </div>

              <div className="worklists__col">
              <section className="panel">
                <h2>
                  Isporuke kupcima{' '}
                  {deliveries.length > 0 && (
                    <span className="muted">· {deliveries.filter((d) => d.canBePlanned).length} spremno</span>
                  )}
                </h2>
                {loading ? (
                  <p className="muted">Učitavam…</p>
                ) : (
                  <DeliveryPanel
                    deliveries={deliveries}
                    selected={selected.filter((k) => k.startsWith(KUPAC)).map((k) => k.slice(KUPAC.length))}
                    onToggle={(documentKey) => toggle(KUPAC + documentKey)}
                  />
                )}
              </section>
              </div>
            </div>

            <section className="panel">
              <h2>
                Tura {selected.length > 0 && <span className="muted">· {selected.length} stanica</span>}
              </h2>
              <TemplatePicker templates={templates} onApply={(t) => void applyTemplate(t)} />
              <TourBuilder
                stops={selectedStops}
                vehicles={vehicles}
                drivers={drivers}
                locations={locations}
                fillThreshold={fillThreshold}
                onMove={move}
                onReorder={reorder}
                onRemove={(d) => setSelected((c) => c.filter((x) => x !== d))}
                onSaved={() => {
                  setSelected([]);
                  setProposal(null);
                  void load();
                }}
              />
            </section>
          </div>
        </>
      )}
    </div>
  );
}
